import { useEffect, useRef, useState } from 'react';
import Beacon from './Beacon.jsx';
import { Button, Card } from './primitives.jsx';

export const CHECKOUT_CONFIRMATION_TIMEOUT_MS = 30_000;
const CHECKOUT_CONFIRMATION_POLL_MS = 2_000;

// A prior refunded/expired subscription can remain the server's last snapshot
// while its replacement payment catches up. Only a changed restriction ends
// the cosmetic wait early; unchanged history is exposed after the deadline.
export function checkoutRestrictionSignature(billing) {
  if (!billing || (!['refunded', 'disputed', 'expired', 'grace_expired', 'past_due'].includes(billing.status) && !['pending', 'failed'].includes(billing.pauseStatus))) return null;
  return JSON.stringify([billing.status, billing.cycle, billing.currentPeriodEnd, billing.accessExpiresAt, billing.graceEndsAt, billing.cancelAtPeriodEnd, billing.pauseStatus]);
}

// This is a presentation-only wait for the existing company billing read.
// A Checkout return, SDK result or timer never grants campaign permission.
export default function BillingCheckoutConfirmation({ refresh, readStatus, billing, onBrowse, children }) {
  const [attempt, setAttempt] = useState(0);
  const [checking, setChecking] = useState(true);
  const latestReadStatus = useRef(readStatus);
  latestReadStatus.current = readStatus;
  const stopCurrent = useRef(null);
  const baseline = useRef({ captured: Boolean(billing), signature: checkoutRestrictionSignature(billing) });
  const latestBilling = useRef(billing);
  latestBilling.current = billing;

  useEffect(() => {
    let stopped = false;
    let nextPoll, deadline;
    setChecking(true);
    function stop() {
      if (stopped) return;
      stopped = true;
      clearTimeout(nextPoll);
      clearTimeout(deadline);
      setChecking(false);
    }
    stopCurrent.current = stop;
    // Bound the visible wait even if an individual network read never settles.
    deadline = setTimeout(stop, CHECKOUT_CONFIRMATION_TIMEOUT_MS);
    async function poll() {
      if (stopped) return;
      try {
        const snapshot = await refresh();
        if (stopped) return;
        // A superseded company read also returns null. It is not evidence of
        // failure: the provider's authoritative error state distinguishes it.
        if (!snapshot && latestReadStatus.current === 'error') { stop(); return; }
        if (snapshot?.billing) {
          const signature = checkoutRestrictionSignature(snapshot.billing);
          if (!baseline.current.captured) baseline.current = { captured: true, signature };
          else if (signature && signature !== baseline.current.signature) { stop(); return; }
        }
        nextPoll = setTimeout(poll, CHECKOUT_CONFIRMATION_POLL_MS);
      } catch {
        // Keep uncertainty explicit if a caller cannot complete the read.
        if (!stopped) { clearTimeout(deadline); stop(); }
      }
    }
    poll();
    return () => { stopped = true; clearTimeout(deadline); clearTimeout(nextPoll); if (stopCurrent.current === stop) stopCurrent.current = null; };
  }, [refresh, attempt]);

  // Observe an actual failed read without restarting the wall-clock deadline
  // when ordinary loading/ready snapshots change during verification.
  useEffect(() => { if (readStatus === 'error') stopCurrent.current?.(); }, [readStatus]);

  function retry() {
    baseline.current = { captured: Boolean(latestBilling.current), signature: checkoutRestrictionSignature(latestBilling.current) };
    setChecking(true);
    setAttempt(value => value + 1);
  }

  // This read-only wait must not trigger the shell's mutation-in-progress
  // company-switch guard. The Beacon supplies its own accessible status.
  const notice = <Card style={{ padding: 32 }}><div className="bl-billing-confirmation" aria-live="polite">
    {checking ? <Beacon size="page" label="Checking subscription status" delayMs={0} slowText="Payment confirmation is taking a little longer. You can keep browsing while we check." /> : <h2 role="status">Paid access has not been confirmed</h2>}
    <p>{checking ? 'We’re confirming your payment and updating your company’s subscription. Please don’t start another purchase while we check.' : 'Check subscription status again before starting another checkout. If your bank shows a charge, do not pay again while confirmation is pending.'}</p>
    <div className="bl-billing-actions"><Button size="sm" variant="ghost" disabled={checking} onClick={retry}>Check subscription status</Button><Button size="sm" variant="ghost" onClick={onBrowse}>Keep browsing</Button></div>
  </div></Card>;
  return children ? children(notice, checking) : notice;
}
